export const neptuneTree = {
  id: 'neptune',
  label: 'Neptune',
  type: 'Ice giant',
  summary: 'The blue frontier: windiest planet, most distant from the Sun.',
  payload: [
    {
      type: 'description',
      text: "Neptune is the eighth and farthest planet from the Sun, the fourth-largest by diameter, and the third-most-massive. It's an ice giant, compositionally similar to Uranus: a hydrogen-helium atmosphere over a mantle of water, methane, and ammonia ices. Its blue colour comes from methane in the upper atmosphere absorbing red light. Voyager 2\'s famous deep-blue portraits were contrast-enhanced, and a 2024 reprocessing showed Neptune and Uranus are much closer in colour than those images suggest, with Neptune only slightly bluer. Winds in the upper atmosphere reach 2,100 km/h, the fastest in the solar system."
    },
    {
      type: 'stats',
      label: 'At a glance',
      entries: [
        { label: 'Diameter',          value: '49,244 km (3.86 × Earth)' },
        { label: 'Mass',              value: '1.024 × 10²⁶ kg (17.1 × Earth)' },
        { label: 'Distance from Sun', value: '4,515 M km (30.18 AU)' },
        { label: 'Orbital period',    value: '164.8 Earth years' },
        { label: 'Day length',        value: '16h 06m' },
        { label: 'Axial tilt',        value: '28.32°' },
        { label: 'Moons',             value: '16 known' },
        { label: 'Temp at 1 bar',     value: '−201 °C' }
      ]
    }
  ],
  children: [
    {
      id: 'vital-stats',
      label: 'Vital statistics',
      summary: 'Size, density, gravity, magnetic field',
      payload: [
        {
          type: 'stats',
          label: 'Physical',
          entries: [
            { label: 'Equatorial radius', value: '24,764 km' },
            { label: 'Mean density',      value: '1.64 g/cm³' },
            { label: 'Surface gravity',   value: '11.27 m/s² (1.15 g, at 1-bar level)' },
            { label: 'Escape velocity',   value: '23.5 km/s' }
          ]
        },
        {
          type: 'stats',
          label: 'Magnetic',
          entries: [
            { label: 'Dipole tilt',    value: '47° from rotation axis' },
            { label: 'Offset',         value: 'Off-centred by about half the planet\'s radius' }
          ]
        },
        {
          type: 'callout',
          tone: 'info',
          text: "Neptune radiates 2.6× more heat than it receives from the Sun, so it has an internal heat source, probably left over from formation. Uranus does not. Why the two ice giants differ here is still unexplained."
        }
      ]
    },
    {
      id: 'atmosphere',
      label: 'Atmosphere',
      summary: 'Deep blue, supersonic winds, Great Dark Spot',
      payload: [
        {
          type: 'description',
          text: "Neptune's upper atmosphere is about 80% hydrogen, 19% helium, and 1.5% methane, with trace amounts of hydrogen deuteride and ethane. The methane absorbs red light, giving Neptune its blue colour. It's the most dynamic ice-giant atmosphere in the solar system. Voyager 2 spotted the 'Great Dark Spot' in 1989, an anticyclonic storm large enough to contain the entire Earth. By 1994 Hubble found the spot had dissipated. Fresh dark spots have appeared and disappeared since."
        },
        {
          type: 'composition-bars',
          entries: [
            { name: 'Hydrogen (H₂)', percent: 80,  color: '#2a6ba8' },
            { name: 'Helium (He)',   percent: 19,  color: '#6fb2d4' },
            { name: 'Methane (CH₄)', percent: 1.5, color: '#1a4d80' }
          ]
        },
        {
          type: 'callout',
          tone: 'warn',
          text: "Wind speeds on Neptune hit about 2,100 km/h (up to 580 m/s). That's the fastest recorded wind in the solar system, on the planet furthest from the Sun and therefore with the least solar energy input. No one has a complete theory for why."
        }
      ]
    },
    {
      id: 'moons',
      label: 'Moons',
      summary: 'Triton dominates: captured, retrograde, probably a Kuiper Belt refugee',
      payload: [
        {
          type: 'list',
          entries: [
            { primary: 'Triton',  secondary: '2,705 km, 7th largest moon in the solar system, orbits retrograde, likely a captured KBO' },
            { primary: 'Nereid',  secondary: '340 km, with an extremely eccentric orbit (e=0.75) that suggests capture or major perturbation' },
            { primary: 'Naiad, Thalassa, Despina, Galatea, Larissa, Proteus', secondary: 'Inner moons discovered by Voyager 2' },
            { primary: 'Hippocamp', secondary: '~35 km, a tiny moon hidden in Proteus\'s orbit, found in 2013' }
          ]
        },
        {
          type: 'description',
          text: "Triton is a world unto itself. It's one of only a handful of moons in the solar system with any atmosphere at all (a thin one, mostly nitrogen), has active cryovolcanism (nitrogen geysers spotted by Voyager 2), and its retrograde orbit strongly suggests it was captured rather than formed in place. It's slowly spiraling inward and will eventually be torn apart by Neptune's tides, in about 3.6 billion years."
        }
      ]
    },
    {
      id: 'rings',
      label: 'Rings',
      summary: 'Five faint, clumpy rings',
      payload: [
        {
          type: 'description',
          text: "Neptune has five confirmed rings, all faint and much darker than Saturn's. The outer Adams ring contains unusual 'arcs', clumps of denser material that should, by all accounts, have spread out into a uniform ring long ago. They're held in place by resonance with the moon Galatea. The arcs are named after the French motto: Liberté, Égalité, Fraternité, and Courage."
        }
      ]
    },
    {
      id: 'exploration',
      label: 'Exploration',
      summary: 'Voyager 2 in 1989, still the only close encounter',
      payload: [
        {
          type: 'description',
          text: "Voyager 2 flew within 4,950 km of Neptune's cloud tops on August 25, 1989, the closest approach to any planet in its entire tour. It discovered six new moons, four rings, the Great Dark Spot, and Triton's cryovolcanism. Voyager 2 remains the only spacecraft to have visited Neptune; everything else we know has come from Earth-based telescopes, Hubble, and JWST."
        },
        {
          type: 'callout',
          tone: 'info',
          text: "At Neptune's distance, the Sun is 1/900th as bright as it is on Earth. The planet still receives enough energy to power its storms, but only just."
        }
      ]
    },
    {
      id: 'discovery',
      label: 'Discovery',
      summary: 'Predicted by math before it was seen',
      payload: [
        {
          type: 'description',
          text: "Neptune is the only planet in the solar system found by mathematical prediction rather than direct observation. Irregularities in Uranus's orbit led Urbain Le Verrier (France) and John Couch Adams (England) to independently predict a perturbing body beyond Uranus in the 1840s. Johann Galle at the Berlin Observatory pointed his telescope at Le Verrier's predicted coordinates on the night of September 23, 1846, and found Neptune within 1° of the prediction."
        }
      ]
    },
    {
      id: 'references',
      label: 'External references',
      summary: 'Where to dig deeper',
      payload: [
        {
          type: 'links',
          entries: [
            { label: 'NASA Neptune fact sheet',  url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/neptunefact.html' },
            { label: 'Wikipedia: Neptune',        url: 'https://en.wikipedia.org/wiki/Neptune' },
            { label: 'Voyager 2 Neptune flyby',   url: 'https://science.nasa.gov/mission/voyager/' },
            { label: 'NASA: Neptune facts',       url: 'https://science.nasa.gov/neptune/neptune-facts/' },
            { label: 'JPL: satellite physical parameters', url: 'https://ssd.jpl.nasa.gov/sats/phys_par/' }
          ]
        }
      ]
    }
  ]
};
